import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import { useLogin } from "@/api/auth";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Field,
	FieldError,
	FieldGroup,
	FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/auth-context";
import { type LoginFormValues, loginSchema } from "@/lib/schemas/auth.schema";
import { cn } from "@/lib/utils";
import { getApiErrorKey, setDrfErrors } from "@/utils/errors";

export function LoginForm({
	className,
	...props
}: React.ComponentProps<"div">) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { lang } = useParams<{ lang: string }>();
	const { login } = useAuth();
	const loginMutation = useLogin();

	const [showPassword, setShowPassword] = useState(false);

	const {
		register,
		handleSubmit,
		setError,
		formState: { errors },
	} = useForm<LoginFormValues>({ resolver: zodResolver(loginSchema) });

	const onSubmit = (data: LoginFormValues) => {
		loginMutation.mutate(data, {
			onSuccess: (response) => {
				toast.success(t("login.success"));
				login(response, data.username);
				navigate(`/${lang ?? "pl"}/`);
			},
			onError: (error) => {
				if (error.message === "rate_limited") return;
				setDrfErrors(error, setError);
				toast.error(t(getApiErrorKey(error)));
			},
		});
	};

	return (
		<div className={cn("flex flex-col gap-6", className)} {...props}>
			<Card>
				<CardHeader>
					<CardTitle>{t("login.title")}</CardTitle>
					<CardDescription>{t("login.description")}</CardDescription>
				</CardHeader>
				<CardContent>
					<form onSubmit={handleSubmit(onSubmit)}>
						<FieldGroup>
							<Field>
								<FieldLabel htmlFor="username">
									{t("login.username")}
								</FieldLabel>
								<Input
									id="username"
									type="text"
									autoComplete="username"
									spellCheck={false}
									placeholder={t("login.usernamePlaceholder")}
									{...register("username")}
								/>
								{errors.username && (
									<FieldError>{errors.username.message}</FieldError>
								)}
							</Field>
							<Field>
								<div className="flex items-center">
									<FieldLabel htmlFor="password">
										{t("login.password")}
									</FieldLabel>
									<Link
										to={`/${lang ?? "pl"}/forgot-password`}
										className="ml-auto inline-block text-sm underline-offset-4 hover:underline"
									>
										{t("login.forgotPassword")}
									</Link>
								</div>
								<div className="relative">
									<Input
										id="password"
										type={showPassword ? "text" : "password"}
										autoComplete="current-password"
										className="pr-9"
										{...register("password")}
									/>
									<Button
										type="button"
										variant="ghost"
										size="icon-xs"
										onClick={() => setShowPassword((v) => !v)}
										className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:bg-transparent hover:text-foreground focus-visible:bg-transparent"
										tabIndex={-1}
										aria-label={
											showPassword
												? t("login.hidePassword")
												: t("login.showPassword")
										}
									>
										{showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
									</Button>
								</div>
								{errors.password && (
									<FieldError>{errors.password.message}</FieldError>
								)}
							</Field>
							<Field>
								<Button
									type="submit"
									disabled={
										loginMutation.isPending || loginMutation.isRateLimited
									}
								>
									{loginMutation.isRateLimited
										? t("login.rateLimited", {
												seconds: loginMutation.retryAfter,
											})
										: loginMutation.isPending
											? t("login.loading")
											: t("login.submit")}
								</Button>
								{/* <FieldDescription className="text-center">
									{t("login.noAccount")}{" "}
									<Link to="/signup">{t("login.signup")}</Link>
								</FieldDescription> */}
							</Field>
						</FieldGroup>
					</form>
				</CardContent>
			</Card>
		</div>
	);
}
